// src/components/ClientInventoryTable.tsx
import { EnvironmentOutlined } from "@ant-design/icons";
import { Table, TableColumnsType, Tag, Button, Tabs, Card, Empty, Modal } from "antd";
import { TClient, TInventory } from "~/types";
import { capitalize, formatDimensions, getClientName } from "~/utils";
import { useExportInventory } from "~/services/inventory";
import { useNotification } from "~/hooks";
import { Download, Calendar, History as HistoryIcon, X } from "lucide-react";
import { InventoryStatusCode } from "~/utils/constants";
import { useState, FC } from "react";
import { useGetHistoryServiceTicketsByInventoryId, useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";
import { useNavigate } from "react-router-dom";

interface ClientInventoryTableProps {
  client: TClient;
  clientInventory?: TInventory[];
}

const InventoryDetailModal: FC<{
  inventory: TInventory | null;
  open: boolean;
  onClose: () => void;
}> = ({ inventory, open, onClose }) => {
  const navigate = useNavigate();
  const { data: historyData } = useGetHistoryServiceTicketsByInventoryId(
    inventory?.id || ""
  );
  const { data: upcomingData } = useGetUpcomingServiceTicketsByInventoryId(
    inventory?.id || ""
  );

  const handleRowClick = (serviceRecord: any) => {
    const bookingId = serviceRecord.jobTicket?.id;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${serviceRecord.id}`);
      onClose();
    }
  };

  const serviceColumns = [
    {
      title: "Ticket #",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <span className="font-semibold">{text}</span>,
    },
    {
      title: "Service Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: string) => capitalize(text),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: string) => <Tag>{capitalize(text)}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => date ? new Date(date).toLocaleDateString() : "N/A",
    },
  ];

  return (
    <Modal
      title={
        <div>
          <div className="text-lg font-medium">Service Tickets</div>
          {inventory && (
            <div className="text-sm text-gray-500 font-normal mt-1">
              {inventory.title} - {inventory.inventoryRef}
            </div>
          )}
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      closeIcon={<X size={20} />}
    >
      <Tabs
        defaultActiveKey="history"
        items={[
          {
            key: "history",
            label: (
              <span className="flex items-center gap-2">
                <HistoryIcon size={14} />
                History ({historyData?.items?.length || 0})
              </span>
            ),
            children: historyData?.items?.length ? (
              <Table
                dataSource={historyData.items}
                columns={serviceColumns}
                rowKey="id"
                size="small"
                pagination={false}
                onRow={(serviceRecord) => ({
                  onClick: () => handleRowClick(serviceRecord),
                  style: { cursor: "pointer" },
                })}
              />
            ) : (
              <Empty description="No service history" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          },
          {
            key: "upcoming",
            label: (
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                Upcoming ({upcomingData?.items?.length || 0})
              </span>
            ),
            children: upcomingData?.items?.length ? (
              <Table
                dataSource={upcomingData.items}
                columns={serviceColumns}
                rowKey="id"
                size="small"
                pagination={false}
                onRow={(serviceRecord) => ({
                  onClick: () => handleRowClick(serviceRecord),
                  style: { cursor: "pointer" },
                })}
              />
            ) : (
              <Empty description="No upcoming services" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          },
        ]}
      />
    </Modal>
  );
};

export const ClientInventoryTable: React.FC<ClientInventoryTableProps> = ({
  client,
  clientInventory,
}) => {
  const [selectedInventory, setSelectedInventory] = useState<TInventory | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Export functionality
  const exportMutation = useExportInventory();
  const { success, error } = useNotification();

  const handleRowClick = (record: TInventory) => {
    setSelectedInventory(record);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInventory(null);
  };

  const handleExportClientInventory = () => {
    exportMutation.mutate(
      { clientId: client.id, status: InventoryStatusCode.AVAILABLE_ON_HAND },
      {
        onSuccess: () => {
          success({
            message: "Export successful",
            description: `Inventory data for ${
              client.companyName || getClientName(client)
            } has been exported to CSV`,
          });
        },
        onError: (err) => {
          error({
            message: "Export failed",
            description:
              err.message || "There was an error exporting the inventory data",
          });
        },
      }
    );
  };
  const columns: TableColumnsType<TInventory> = [
    {
      title: "Title",
      dataIndex: "title",
      render: (title, record) => (
        <div>
          <div>{title}</div>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>
            {capitalize(record.itemType)}
          </div>
        </div>
      ),
    },
    {
      title: "Dimensions",
      dataIndex: "dimensions",
      width: "10%",
      align: "center",
      render: (_, record) => {
        const dimensions = formatDimensions(record);
        return dimensions;
      },
    },
    {
      title: "Client Ref",
      dataIndex: "clientInternalRef",
      width: "15%",
      align: "center",
      render: (clientInternalRef) => clientInternalRef || "N/A",
    },
    {
      title: "AHC Ref",
      dataIndex: "ahcRef",
      width: "10%",
      align: "center",
      render: (_, record) => (
        <div style={{ fontSize: 12, color: "#8c8c8c" }}>
          {record.inventoryRef}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: "15%",
      align: "center",
      render: (status: string) => (
        <Tag
          color={
            status.includes("Available")
              ? "green"
              : status === "In Transit"
              ? "yellow"
              : "blue"
          }
        >
          {capitalize(status)}
        </Tag>
      ),
    },
    {
      title: "Location",
      dataIndex: "location",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {record.location?.name || "N/A"}
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h3 style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
          {client.companyName} - Inventory Items
        </h3>
        <Button
          size="small"
          icon={<Download size={14} />}
          onClick={handleExportClientInventory}
          loading={exportMutation.isPending}
          disabled={!clientInventory || clientInventory.length === 0}
          style={{ display: "flex", alignItems: "center" }}
        >
          Export Client Inventory
        </Button>
      </div>
      <Table
        bordered
        size="small"
        columns={columns}
        dataSource={clientInventory}
        rowKey="id"
        pagination={false}
        locale={{
          emptyText: "No inventory items found for this client",
        }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
      />

      <InventoryDetailModal
        inventory={selectedInventory}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};
